import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import IframeResizer from 'iframe-resizer-react';
import ZoomableImageView from '../../ZoomableImage/ZoomableImageView';
import { FakturabildFormat } from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/VerifikationQuery/VerifikationQuery';
import {
  getBaseZoom,
  getTranslateCenter,
} from '../../ZoomableImage/Utils/ZoomUtils';
import classNames from 'clsx';
import { useIsSmallScreen } from '../../../Hooks/useFullscreenView';
import EDIcon from '../../../BaseComponents/Icon/EDIcon';
import EDIconButton from '../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { useAttributeValues } from '../../../Features/Delsystem/_Common/Hooks/useAttributeValues';
import {
  FLOATING_MENU_CLEARANCE,
  FLOATING_MENU_SAFE_AREA_PADDING_RIGHT,
  useIsFloatingMenuVisible,
} from '../../FloatingBottomArea/FloatingMenu';

interface IProps {
  extraBottomPadding?: boolean;
  noFullscreenToggle?: boolean;
  title: string;
  url: string;
  useWheelToScroll: boolean;
  verifikationsNr?: string;
  xmlFakturaFormat?: FakturabildFormat;
  ignoreFloatingMenu?: boolean;
  classes?: any;
}

export interface IDimensions {
  height: number;
  width: number;
}

const styles = (theme: Theme) => ({
  button: {
    position: 'sticky',
    left: '8px',
    bottom: '8px',
  },
  container: {
    position: 'relative',
    width: '100%',
  },
  iconButton: {
    backgroundColor: theme.palette.primary.main,

    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  iconButtonFloatingMenuVisible: {
    marginBottom: FLOATING_MENU_CLEARANCE,
    marginRight: FLOATING_MENU_SAFE_AREA_PADDING_RIGHT,
  },
  overlay: {
    alignItems: 'flex-end',
    display: 'flex',
    height: '100%',
    justifyContent: 'flex-end',
    position: 'absolute',
    right: 0,
    top: 0,
  },
  iframe: {
    border: 'none',
    background: theme.palette.common.white,
    minWidth: '100%',
  },
  iframeKob: {
    pointerEvents: 'none',
    width: '582px !important',
  },
  iframeSvefaktura: {
    width: '742px !important',
  },
});

const openFakturaInNewWindow = (
  blobUrl: string,
  verifikationsNr: string | undefined
) => {
  fetch(blobUrl, {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'text/html',
    },
  })
    .then(response => {
      const result = response.text();

      return result;
    })
    .then(data => {
      const newWindow = window.open(
        '',
        '_blank',
        'toolbar=yes,scrollbars=yes,resizable=yes'
      );

      if (newWindow) {
        newWindow.document.write(data);

        const button = newWindow.document.createElement('button');

        const nav = newWindow.document.createElement('nav');

        const styling = newWindow.document.createElement('style');

        if (verifikationsNr) {
          const vernrContainer = newWindow.document.createElement('div');
          vernrContainer.classList.add('vernr-container');
          vernrContainer.innerHTML = verifikationsNr
            ? `<p>Verifikationsnr: ${verifikationsNr}</p>`
            : '';
          newWindow.document.body.prepend(vernrContainer);
        }

        if (styling) {
          styling.innerHTML = `
            nav {
              background-color: white;
              border: 1px solid black;
              box-shadow: 0 0 5px 2px black;
              height: 20px;
              left: 0;
              padding: 4px;
              position: fixed;
             
              top: 0;
              width: 100%;
            }

            table {
              margin-top: 40px;
            }

            .vernr-container {
              display: none;
            }


            @media print {
              table {
                margin-top: 12px;
              }
              tr {
                page-break-inside: avoid;
              }
              nav{
                display: none;
              }

              .vernr-container{
                position: absolute;
                top: -12px;
                right: 2px;
                display: flex;
                justify-content: flex-end;
                font-size: 16px;
              }
            }
          `;

          newWindow?.document.body.appendChild(styling);
        }

        if (button && nav) {
          button.addEventListener('click', () => {
            newWindow?.print();
          });
          button.innerHTML = 'Skriv ut';
          nav.appendChild(button);
          newWindow?.document.body.appendChild(nav);
        }
      }
    });
};

const XmlFaktura: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [paperRef, setPaperRef] = useState<HTMLDivElement>();
  const [paperRefFullscreen, setPaperRefFullscreen] =
    useState<HTMLDivElement>();
  const [iFrameDimensions, setIFrameDimensions] = useState<IDimensions>();
  const [paperDimensions, setPaperDimensions] = useState<IDimensions>();
  const [paperDimensionsFullscreen, setPaperDimensionsFullscreen] =
    useState<IDimensions>();

  const isSvefaktura = props.xmlFakturaFormat === 'SVEFAKTURA';
  const isKob = props.xmlFakturaFormat === 'KOB';
  const isFloatingMenuVisible =
    useIsFloatingMenuVisible() && !props.ignoreFloatingMenu;

  useEffect(() => {
    if (paperRef) {
      const dimensions = {
        width: paperRef.offsetWidth,
        height: paperRef.offsetHeight,
      };

      setPaperDimensions(dimensions);

      window.addEventListener('resize', () => {
        setPaperDimensions(dimensions);
      });
    }
  }, [paperRef]);

  useEffect(() => {
    if (paperRefFullscreen) {
      const dimensions = {
        width: paperRefFullscreen.offsetWidth,
        height: paperRefFullscreen.offsetHeight,
      };

      setPaperDimensionsFullscreen(dimensions);

      window.addEventListener('resize', () => {
        setPaperDimensionsFullscreen(dimensions);
      });
    }
  }, [paperRefFullscreen]);

  const isSmallScreen = useIsSmallScreen();

  const scale =
    paperDimensions && iFrameDimensions
      ? getBaseZoom(paperDimensions, iFrameDimensions)
      : 1;
  const translateX =
    paperDimensions && iFrameDimensions
      ? getTranslateCenter(paperDimensions.width, iFrameDimensions.width, scale)
      : 0;
  const translateY =
    paperDimensions && iFrameDimensions
      ? getTranslateCenter(
          paperDimensions.height,
          iFrameDimensions.height,
          scale
        )
      : 0;

  const scaleFullscreen =
    paperDimensionsFullscreen && iFrameDimensions
      ? getBaseZoom(paperDimensionsFullscreen, iFrameDimensions)
      : 1;
  const translateXFullscreen =
    paperDimensionsFullscreen && iFrameDimensions
      ? getTranslateCenter(
          paperDimensionsFullscreen.width,
          iFrameDimensions.width,
          scaleFullscreen
        )
      : 0;
  const translateYFullscreen =
    paperDimensionsFullscreen && iFrameDimensions
      ? getTranslateCenter(
          paperDimensionsFullscreen.height,
          iFrameDimensions.height,
          scaleFullscreen
        )
      : 0;

  const iFrameClassName = classNames(props.classes.iframe, {
    [props.classes.iframeSvefaktura]: isSvefaktura,
    [props.classes.iframeKob]: isKob,
  });

  let value = props.verifikationsNr;

  if (!value) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    value = useAttributeValues('Faktura.DefAnkVernr').value;
  }

  return (
    <>
      {isSvefaktura ? (
        <div
          className={classNames(props.classes.container, {
            [props.classes.iframeSvefaktura]: isSmallScreen,
          })}
          style={
            isFloatingMenuVisible
              ? { paddingBottom: FLOATING_MENU_CLEARANCE }
              : undefined
          }
        >
          <IframeResizer src={props.url} className={iFrameClassName} />
          <span className={props.classes.overlay}>
            <span className={props.classes.button}>
              <EDIconButton
                className={classNames(props.classes.iconButton, {
                  [props.classes.iconButtonFloatingMenuVisible]:
                    isFloatingMenuVisible,
                })}
                icon={<EDIcon iconType="open_in_new" color="white" />}
                onClick={() => {
                  openFakturaInNewWindow(props.url, value);
                }}
                tooltip="Öppna i nytt fönster"
              />
            </span>
          </span>
        </div>
      ) : (
        <ZoomableImageView
          centeredImage
          imageCount={1}
          imageIndex={0}
          isImage={false}
          nextImage={() => {}}
          previousImage={() => {}}
          noImageCount
          useWheelToScroll={props.useWheelToScroll}
          title={props.title}
          subtitle="FAKTURABILD"
          noFullscreenToggle={props.noFullscreenToggle}
          extraBottomPadding={props.extraBottomPadding}
          ignoreFloatingMenu={props.ignoreFloatingMenu}
          navHeader="Fakturabild"
          setPaperRef={setPaperRef}
          setPaperRefFullscreen={setPaperRefFullscreen}
          fullScreenRender={
            <IframeResizer
              src={props.url}
              style={{
                transform: `scale(${scaleFullscreen}) translateX(${translateXFullscreen}px) translateY(${translateYFullscreen}px)`,
              }}
              className={iFrameClassName}
            />
          }
        >
          <IframeResizer
            src={props.url}
            style={{
              transform: `scale(${scale}) translateX(${translateX}px) translateY(${translateY}px)`,
            }}
            className={iFrameClassName}
            onResized={(event: any) => {
              setIFrameDimensions({ width: event.width, height: event.height });
            }}
          />
        </ZoomableImageView>
      )}
    </>
  );
};

export default withStyles(XmlFaktura, styles);
